/**
 * MIRA Dual Input Component
 * Allows switching between dollar and percentage input
 * Used for down payment, closing costs, etc.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip';
import styles from '@/styles/components/dual-input.module.css';

export const DualInput = ({
  value = 0,
  referenceValue = 0, // Sales price or other reference for percentage calculation
  onChange,
  error,
  disabled = false,
  required = false,
  label = 'Amount',
  helperText = 'Click to switch between $ and %',
  helperDisplay = 'inline',
  onFocus,
  onBlur,
}) => {
  const [inputMode, setInputMode] = useState('dollar'); // 'dollar' or 'percentage'
  const [isFocused, setIsFocused] = useState(false);
  const [dollarValue, setDollarValue] = useState(value || 0);
  const [percentValue, setPercentValue] = useState(0);
  const dollarInputRef = useRef(null);
  const percentInputRef = useRef(null);

  // Calculate percentage from dollar amount
  const calculatePercentage = (dollarAmount, price) => {
    if (!price || price === 0) return 0;
    return ((dollarAmount / price) * 100).toFixed(1);
  };

  // Calculate dollar amount from percentage
  const calculateDollar = (percentage, price) => {
    if (!price) return 0;
    return Math.round(price * (percentage / 100));
  };

  // Initialize percentage when component mounts or value changes
  useEffect(() => {
    if (referenceValue > 0) {
      const percent = calculatePercentage(value || dollarValue, referenceValue);
      setPercentValue(parseFloat(percent));
    }
  }, [value, referenceValue]);

  // Update dollar value when prop changes
  useEffect(() => {
    setDollarValue(value || 0);
  }, [value]);

  const focusInput = (ref) => {
    requestAnimationFrame(() => {
      if (ref?.current) {
        ref.current.focus();
        if (typeof ref.current.select === 'function') {
          ref.current.select();
        }
      }
    });
  };

  // Handle reference value changes - preserve user's input mode
  useEffect(() => {
    if (!referenceValue || referenceValue === 0) return;

    if (inputMode === 'dollar') {
      // User entered dollars - preserve it, recalculate %
      const newPercent = calculatePercentage(dollarValue, referenceValue);
      setPercentValue(parseFloat(newPercent));
    } else {
      // User entered % - preserve it, recalculate dollars
      const newDollar = calculateDollar(percentValue, referenceValue);
      setDollarValue(newDollar);
      onChange?.(newDollar);
    }
  }, [referenceValue]);

  // Handle dollar input change
  const handleDollarChange = (e) => {
    const newDollar = parseFloat(e.target.value) || 0;
    setDollarValue(newDollar);
    setInputMode('dollar');

    // Calculate percentage
    const newPercent = calculatePercentage(newDollar, referenceValue);
    setPercentValue(parseFloat(newPercent));

    // Notify parent
    onChange?.(newDollar);
  };

  // Handle percentage input change
  const handlePercentChange = (e) => {
    const newPercent = parseFloat(e.target.value) || 0;
    setPercentValue(newPercent);
    setInputMode('percentage');

    // Calculate dollar amount
    const newDollar = calculateDollar(newPercent, referenceValue);
    setDollarValue(newDollar);

    // Notify parent
    onChange?.(newDollar);
  };

  // Switch to dollar mode
  const switchToDollarMode = () => {
    if (inputMode !== 'dollar') {
      setInputMode('dollar');
    }
    focusInput(dollarInputRef);
  };

  // Switch to percentage mode
  const switchToPercentMode = () => {
    if (inputMode !== 'percentage') {
      setInputMode('percentage');
    }
    focusInput(percentInputRef);
  };

  const activateDollarMode = (e) => {
    e.preventDefault();
    if (disabled) {
      return;
    }
    switchToDollarMode();
  };

  const activatePercentMode = (e) => {
    e.preventDefault();
    if (disabled) {
      return;
    }
    switchToPercentMode();
  };

  // Format percentage for display - whole numbers without decimals
  const displayPercentage = percentValue % 1 === 0
    ? Math.round(percentValue)
    : Math.round(percentValue * 10) / 10; // Round to 1 decimal

  return (
    <div className={styles.dualInputField}>
      {/* Label */}
      <div className={styles.labelRow}>
        <label className={styles.label}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
        {helperText && helperDisplay === 'tooltip' && (
          <TooltipProvider delayDuration={120}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={styles.helperIconButton}
                  aria-label={`${label} help`}
                >
                  <Info className={styles.helperIcon} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" sideOffset={8}>
                {helperText}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Dual Input Container */}
      <div className={styles.dualInputContainer}>
        {/* Dollar Input */}
        <div
          className={`${styles.inputWrapper} ${isFocused && inputMode === 'dollar' ? styles.active : styles.calculated}`}
          onMouseDown={activateDollarMode}
        >
          <span className={styles.currencySymbol}>$</span>
          <input
            type="number"
            ref={dollarInputRef}
            value={dollarValue}
            onChange={handleDollarChange}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.();
              e.target.select();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            onClick={(e) => inputMode === 'dollar' && e.stopPropagation()}
            disabled={disabled || inputMode !== 'dollar'}
            className={`${styles.input} ${inputMode === 'dollar' ? styles.inputActive : styles.inputCalculated}`}
            placeholder="0"
          />
        </div>

        {/* Percentage Display/Input */}
        <div
          className={`${styles.inputWrapper} ${styles.percentageWrapper} ${isFocused && inputMode === 'percentage' ? styles.active : styles.calculated}`}
          onMouseDown={activatePercentMode}
        >
          <span className={styles.percentageLabel}>
            {inputMode === 'dollar' ? '(' : ''}
          </span>
          <input
            type="number"
            ref={percentInputRef}
            value={displayPercentage}
            onChange={handlePercentChange}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.();
              e.target.select();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            onClick={(e) => inputMode === 'percentage' && e.stopPropagation()}
            disabled={disabled || inputMode !== 'percentage'}
            className={`${styles.input} ${styles.percentageInput} ${inputMode === 'percentage' ? styles.inputActive : styles.inputCalculated}`}
            placeholder="0"
            step="0.1"
            min="0"
            max="100"
          />
          <span className={styles.percentSymbol}>%{inputMode === 'dollar' ? ')' : ''}</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Helper Text */}
      {!error && helperText && helperDisplay !== 'tooltip' && (
        <div className={styles.helperText}>
          {helperText}
        </div>
      )}
    </div>
  );
};

export default DualInput;
